import { getFreshProductPrices } from './typesense/client/server-client';

export type StockUpdateStatus = 'sold_out' | 'reduced';

export interface StockUpdateChange {
  id: string;
  sku?: string;
  name: string;
  image?: string;
  cartQuantity: number;
  availableStock: number;
  status: StockUpdateStatus;
}

export interface CartStockUpdateResult {
  success: boolean;
  changes: StockUpdateChange[];
  errors: string[];
}

export interface CartItemForStockCheck {
  id: string;
  sku?: string;
  name: string;
  image?: string;
  quantity: number;
  stock?: number;
  pbm_flag?: boolean;
}

/**
 * Service to check current stock for cart items and compute required updates.
 * Used on checkout entry to sync cart quantities with available stock.
 */
export class CartStockUpdateService {
  /**
   * Fetches fresh stock for cart items and returns which items need updating.
   * Does not modify cart; caller applies updates (quantity/stock or remove).
   */
  async checkStock(
    cartItems: CartItemForStockCheck[]
  ): Promise<CartStockUpdateResult> {
    if (cartItems.length === 0) {
      return { success: true, changes: [], errors: [] };
    }

    const nonPBMItems = cartItems.filter((item) => !item?.pbm_flag);
    if (nonPBMItems.length === 0) {
      return { success: true, changes: [], errors: [] };
    }

    const skus = [...new Set(nonPBMItems.map((item) => item.sku || item.id))];

    try {
      const freshData = await getFreshProductPrices(skus);
      const changes: StockUpdateChange[] = [];
      const errors: string[] = [];

      for (const cartItem of nonPBMItems) {
        const skuOrId = cartItem.sku || cartItem.id;
        const fresh = freshData.find((f) => f.id === skuOrId);
        const cartQuantity = cartItem.quantity ?? 1;

        // Skip items with no fresh data to avoid incorrectly marking as sold_out
        if (fresh === undefined) {
          continue;
        }

        const availableStock = fresh.quantity ?? fresh.in_stock ?? 0;

        if (availableStock <= 0) {
          changes.push({
            id: cartItem.id,
            sku: cartItem.sku,
            name: cartItem.name,
            image: cartItem.image,
            cartQuantity,
            availableStock: 0,
            status: 'sold_out',
          });
          continue;
        }

        if (cartQuantity > availableStock) {
          changes.push({
            id: cartItem.id,
            sku: cartItem.sku,
            name: cartItem.name,
            image: cartItem.image,
            cartQuantity,
            availableStock,
            status: 'reduced',
          });
        }
      }

      return {
        success: true,
        changes,
        errors,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro ao verificar estoque';
      console.error('Cart stock update failed:', error);
      return {
        success: false,
        changes: [],
        errors: [errorMessage],
      };
    }
  }
}

export const cartStockUpdateService = new CartStockUpdateService();
